import React, { createContext, useContext, useState } from 'react';

// Context for the form state and configuration
const FormContext = createContext();

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('Form components must be used within AIEmailWriterForm');
  }
  return context;
};

export const FormProvider = ({ children, campaignId, campaignContext, systemPrompt }) => {
  const [formData, setFormData] = useState({});
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [optionGroups, setOptionGroups] = useState([]);
  
  // Register a field component with its configuration
  const registerField = (config) => {
    setFieldConfigs(prev => {
      const filtered = prev.filter(f => f.id !== config.id);
      return [...filtered, config];
    });
  };

  // Register an option group with its options
  const registerOptionGroup = (config) => {
    setOptionGroups(prev => {
      const filtered = prev.filter(g => g.id !== config.id);
      return [...filtered, config];
    });
  };

  // Update form field value
  const updateField = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Get field value
  const getFieldValue = (fieldId) => {
    return formData[fieldId] || '';
  };

  // Check if field has value
  const hasValue = (fieldId) => {
    const value = formData[fieldId];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return Boolean(value);
  };

  // Build user context using registered formatters
  const buildUserContext = () => {
    const context = [];
    
    // Process regular fields
    fieldConfigs.forEach(field => {
      if (field.formatter && hasValue(field.id)) {
        const result = field.formatter(getFieldValue(field.id));
        if (result) context.push(result);
      }
    });

    // Process option groups
    optionGroups.forEach(group => {
      const value = getFieldValue(group.id);
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return;
      }

      if (group.type === 'radio') {
        const selectedOption = group.options.find(opt => opt.value === value);
        if (selectedOption?.formatter) {
          const result = selectedOption.formatter();
          if (result) context.push(result);
        }
      } else if (group.type === 'checkbox') {
        const formatted = value
          .map(val => {
            const option = group.options.find(opt => opt.value === val);
            return option?.formatter ? option.formatter() : null;
          })
          .filter(Boolean);
        
        if (formatted.length > 0) {
          if (group.formatMultiple) {
            context.push(group.formatMultiple(formatted));
          } else {
            formatted.forEach(item => context.push(item));
          }
        }
      }
    });

    return context;
  };

  const value = {
    campaignId,
    campaignContext,
    systemPrompt,
    formData,
    fieldConfigs,
    optionGroups,
    registerField,
    registerOptionGroup,
    updateField,
    getFieldValue,
    hasValue,
    buildUserContext,
  };

  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  );
};